import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosInstance, isEmptyData, logError } from '../../utils/helper';
import { apiUrlB2B } from '../../utils/constants';
import { authHeaders } from '../../utils/helper';

export const getOrderList = createAsyncThunk('order/getOrderList', async (params) => {
	if (isEmptyData(params)) return;
	const res = await axiosInstance.get(`orders/orderList`, { params, ...authHeaders() });
	const data = await res.data;
	return data;
});

export const getOrderListV2 = createAsyncThunk('order/getOrderListV2', async (params) => {
	if (isEmptyData(params)) return;
	const res = await axiosInstance.get(`orders/orderListV2`, { params, ...authHeaders() });
	const data = await res.data;
	return data;
});

export const getOrderDetail = createAsyncThunk('order/getOrderDetail', async (params) => {
	const res = await axiosInstance.get(`orders/orderDetails`, { params, ...authHeaders() });
	const data = await res.data;
	return data;
});

const OrderStatus = {
  PENDING: [0],
  RECEIVED: [1],
  DELIVERING: [2],
  PROCESSING: [3, 13, 14],
  COMPLETED: [4],
  CANCELED: [5],
};

const initialState = {
	all: [],
	pending: [],
	received: [],
	delivering: [],
	processing: [],
	completed: [],
	canceled: [],
	detail: null,
	loading: false,
	error: null,
	params: {
		customerID: null,
		unquieid: null,
		orderNumber: null,
	},
	tab: 'pesanan',

	// order type list
	orderTypeOptions: [],
	isLoadingOrderTypeOptions: false,
	selectedOrderType: {},
	tableNumber: {},
};

// prioritas urutan tampilan order type
const ORDER_TYPE_PRIORITY = {
	delivery: 1,
	pickup: 2,
	dinein: 3,
};

const getOrderTypePriority = (avName = '') => {
	const label = (avName || '').toLowerCase();
	return ORDER_TYPE_PRIORITY[label] ?? 99;
};

export const sortOrderTypeOptions = (options = []) => {
	if (isEmptyData(options)) return [];
	return [...options].sort(
		(a, b) => getOrderTypePriority(a?.av_name) - getOrderTypePriority(b?.av_name),
	);
};

export const getOrderTypeOptions = createAsyncThunk('order/getOrderTypeOptions', async ({ storeID }) => {
	let url = `availability/list`;
	const res = await axiosInstance.post(
		url,
		{ outlet_id: storeID },
		{
			headers: {
				Accept: 'application/json',
        ...authHeaders().headers
			},
		},
	);
	const data = await res.data;
	return data;
});

export const tableNumberAuto = createAsyncThunk(
  "tableNumberAuto",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `${apiUrlB2B}order-ufoodx/create-table-number`,
        payload,
		{
		  headers: {
			...authHeaders().headers,
		  },
		}
      );

	  return {
        ...res?.data?.data,
        expired_at: Date.now() + 4 * 60 * 60 * 1000, // 4 jam
      };
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  }
);

export const updateTableNumberAuto = createAsyncThunk(
  "upateTableNumberAuto",
  async (payload) => {
    console.log("payload", payload);

    const res = await axiosInstance.put(
      `${apiUrlB2B}order-ufoodx/update-table-number`,
      payload
    );
    return res?.data?.data;
  }
);

const groupByOrderStatus = (data = []) => {
	return data.reduce((acc, current) => {
		const status = current.orderStatus;
		if (!acc[status]) {
			acc[status] = [];
		}
		acc[status].push(current);
		return acc;
	}, {});
};

const order = createSlice({
	name: 'order',
	initialState,
	reducers: {
		setOrder: (state, action) => {
			return { ...state, ...action.payload };
		},
		clearOrder: () => {
			return initialState;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getOrderList.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getOrderList.fulfilled, (state, action) => {
				state.loading = false;
				const data = action.payload?.result || [];
				const group = groupByOrderStatus(data);

        const getByStatuses = (group, statuses) => {
          return statuses.flatMap((status) => group[status] || []);
        };
        
				state.all = data;
        state.pending = getByStatuses(group, OrderStatus.PENDING);
        state.received = getByStatuses(group, OrderStatus.RECEIVED);
        state.delivering = getByStatuses(group, OrderStatus.DELIVERING);
        state.processing = getByStatuses(group, OrderStatus.PROCESSING);
        state.completed = getByStatuses(group, OrderStatus.COMPLETED);
        state.canceled = getByStatuses(group, OrderStatus.CANCELED);
			})
			.addCase(getOrderList.rejected, (state, action) => {
				state.loading = false;

        // logError(new Error(action.error?.message), {
        //   feature: "getOrderList",
        //   actionType: action.type,
        // });
			})
      .addCase(getOrderListV2.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getOrderListV2.fulfilled, (state, action) => {
				state.loading = false;
				const data = action.payload?.result || [];

        const getByStatuses = (group, statuses) => {
          return statuses.flatMap((status) => group[status] || []);
        };

				const group = groupByOrderStatus(data);

				state.all = data;
        state.pending = getByStatuses(group, OrderStatus.PENDING);
        state.received = getByStatuses(group, OrderStatus.RECEIVED);
        state.delivering = getByStatuses(group, OrderStatus.DELIVERING);
        state.processing = getByStatuses(group, OrderStatus.PROCESSING);
        state.completed = getByStatuses(group, OrderStatus.COMPLETED);
        state.canceled = getByStatuses(group, OrderStatus.CANCELED);
			})
      .addCase(getOrderListV2.rejected, (state, action) => {
				state.loading = false;

        // logError(new Error(action.error?.message), {
        //   feature: "getOrderListV2",
        //   actionType: action.type,
        // });
			})
			.addCase(getOrderDetail.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(getOrderDetail.fulfilled, (state, action) => {
				state.loading = false;
				state.detail = action.payload.result || null;
			})
			.addCase(getOrderDetail.rejected, (state, action) => {
				state.loading = false;

        // logError(new Error(action.error?.message), {
        //   feature: "getOrderDetail",
        //   actionType: action.type,
        // });
			})
			.addCase(getOrderTypeOptions.pending, (state) => {
				state.isLoadingOrderTypeOptions = true;
			})
			.addCase(getOrderTypeOptions.fulfilled, (state, action) => {
				state.isLoadingOrderTypeOptions = false;
				state.orderTypeOptions = sortOrderTypeOptions(action.payload?.data || []);
			})
			.addCase(getOrderTypeOptions.rejected, (state, action) => {
				state.isLoadingOrderTypeOptions = false;
			
        // logError(new Error(action.error?.message), {
        //   feature: "getOrderTypeOptions",
        //   actionType: action.type,
        // });
      })
      .addCase(tableNumberAuto.fulfilled, (state, action) => {
				state.tableNumber = action.payload;
			});
	},
});

export const { setOrder, clearOrder } = order.actions;
export default order.reducer;
